import React, { useState, useRef, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  IconButton,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

interface ThumbnailUploadProps {
  onUploadStart?: () => void;
  onUploadSuccess?: (thumbnailUrl: string) => void;
  onUploadError?: (error: string) => void;
  onFileSelect?: (file: File) => Promise<string>; // Returns thumbnail URL
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  showPreview?: boolean;
  currentThumbnail?: string;
}

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

const ThumbnailUpload: React.FC<ThumbnailUploadProps> = ({
  onUploadStart,
  onUploadSuccess,
  onUploadError,
  onFileSelect,
  disabled = false,
  size = 'medium',
  showPreview = false,
  currentThumbnail,
}) => {
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [error, setError] = useState<string>('');
  const [preview, setPreview] = useState<string>('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.';
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return 'File size too large. Please upload an image smaller than 5MB.';
    }

    return null;
  };

  const handleFileSelect = useCallback(async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setUploadState('error');
      onUploadError?.(validationError);
      return;
    }

    // Create preview
    if (showPreview) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }

    setUploadState('uploading');
    setError('');
    onUploadStart?.();

    try {
      if (onFileSelect) {
        const thumbnailUrl = await onFileSelect(file);
        setUploadState('success');
        onUploadSuccess?.(thumbnailUrl);
        
        // Reset to idle after 2 seconds
        setTimeout(() => {
          setUploadState('idle');
        }, 2000);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMessage);
      setUploadState('error');
      onUploadError?.(errorMessage);
      setPreview('');
    }
  }, [onFileSelect, onUploadStart, onUploadSuccess, onUploadError, showPreview]);

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };

  const handleClick = () => {
    if (!disabled && uploadState !== 'uploading') {
      fileInputRef.current?.click();
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (!disabled && uploadState !== 'uploading') {
      setDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    
    if (disabled || uploadState === 'uploading') return;

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const getIcon = () => {
    switch (uploadState) {
      case 'uploading':
        return <CircularProgress size={size === 'small' ? 16 : size === 'medium' ? 20 : 24} />;
      case 'success':
        return <CheckCircleIcon sx={{ color: '#4caf50' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: '#f44336' }} />;
      default:
        return <PhotoCameraIcon />;
    }
  };

  const getTooltipText = () => {
    switch (uploadState) {
      case 'uploading':
        return 'Uploading...';
      case 'success':
        return 'Upload successful!';
      case 'error':
        return `Upload failed: ${error}`;
      default:
        return 'Upload thumbnail image';
    }
  };

  const iconSize = size === 'small' ? 20 : size === 'medium' ? 24 : 28;

  return (
    <>
      <UploadContainer
        size={size}
        dragOver={dragOver}
        disabled={disabled || uploadState === 'uploading'}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Tooltip title={getTooltipText()} placement="top">
          <UploadButton
            onClick={handleClick}
            disabled={disabled || uploadState === 'uploading'}
            size={size}
          >
            {getIcon()}
          </UploadButton>
        </Tooltip>
        
        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          onChange={handleFileInputChange}
        />
        
        {showPreview && (preview || currentThumbnail) && (
          <PreviewImage
            src={preview || currentThumbnail}
            alt="Thumbnail preview"
            size={size}
          />
        )}
      </UploadContainer>

      <Snackbar
        open={uploadState === 'error'}
        autoHideDuration={6000}
        onClose={() => setUploadState('idle')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setUploadState('idle')}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

// Styled Components
interface StyledProps {
  size?: 'small' | 'medium' | 'large';
  dragOver?: boolean;
  disabled?: boolean;
}

const UploadContainer = styled(Box)<StyledProps>(({ size, dragOver, disabled }) => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  transition: 'all 0.2s ease-in-out',
  ...(dragOver && !disabled && {
    backgroundColor: 'rgba(24, 99, 211, 0.1)',
    border: '2px dashed #1863D3',
  }),
}));

const UploadButton = styled(IconButton)<StyledProps>(({ size, disabled }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: 'white',
  width: size === 'small' ? 28 : size === 'medium' ? 32 : 36,
  height: size === 'small' ? 28 : size === 'medium' ? 32 : 36,
  '&:hover': {
    backgroundColor: disabled ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.8)',
  },
  '&:disabled': {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    color: 'rgba(255, 255, 255, 0.5)',
  },
}));

const HiddenFileInput = styled('input')({
  display: 'none',
});

const PreviewImage = styled('img')<StyledProps>(({ size }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '4px',
  zIndex: -1,
}));

export default ThumbnailUpload;
